import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { logOut } from './logOut';
import { useAppDispatch } from '../store';
import { useSelector } from "react-redux";

export const useTranslation = (text) => {
  const [translatedText, setTranslatedText] = useState(text);
  const dispatch = useAppDispatch();
  const language = useSelector((state) => state.auth.authData.language);
  const isMounted = useRef(true);
  const fetchingRef = useRef(false);
  const prevLanguageRef = useRef(language);

  const getTranslationFromCache = useCallback((cacheKey) => {
    const translationCache = JSON.parse(localStorage.getItem('translationCache') || '{}');
    return translationCache[cacheKey];
  }, []);

  const saveTranslationToCache = useCallback((cacheKey, translation) => {
    const translationCache = JSON.parse(localStorage.getItem('translationCache') || '{}');
    translationCache[cacheKey] = translation;
    localStorage.setItem('translationCache', JSON.stringify(translationCache));
  }, []);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchTranslation = async () => {
      const cacheKey = `${text}_${language}`;
      const cachedTranslation = getTranslationFromCache(cacheKey);

      if (language === 'en') {
        setTranslatedText(text);
        return;
      }

      if (cachedTranslation) {
        setTranslatedText(cachedTranslation);
        return;
      }

      if (fetchingRef.current) {
        return;
      }

      fetchingRef.current = true;

      try {
        const response = await axios.post(
          'https://cardgarantsales.com/restapi/translate',
          {
            sourcelanguage: 'en',
            sourcetext: text,
            targetlanguage: language,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + localStorage.getItem('access_token'),
            },
          }
        );

        if (isMounted.current) {
          let resultText = response.data && typeof response.data === 'object' && response.data.targettext
            ? response.data.targettext
            : text;

          saveTranslationToCache(cacheKey, resultText);
          setTranslatedText(resultText);
        }
      } catch (error) {
        console.error(error);
        if (error.code === 'ERR_BAD_REQUEST') {
          logOut(dispatch);
        } else if (error.code === 'ERR_NETWORK') {
          console.log('ERR_NETWORK');
        }
        if (isMounted.current) {
          setTranslatedText(text);
        }
      } finally {
        fetchingRef.current = false;
      }
    };

    // Проверяем, изменился ли язык
    if (prevLanguageRef.current !== language) {
      fetchTranslation();
      prevLanguageRef.current = language;
    } else if (!translatedText || translatedText === text) {
      fetchTranslation();
    }
  }, [text, dispatch, language, getTranslationFromCache, saveTranslationToCache]);

  return translatedText;
};

const TranslatedText = ({ text }) => {
  const translatedText = useTranslation(text);
  return translatedText;
};

export default TranslatedText;